/**
 * Audit Events Tab — Admin Client Detail
 * Read-only browser over the immutable audit log (auto_ledger.audit_events).
 * Defaults to Document events; "View history" on a row pins the filters to that
 * entity so the admin sees a document's full change history in one place.
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { useAdminApi } from "@/services/api/admin-api";
import type { AuditEventAdminDetailDto, AuditEventAdminListItemDto } from "@/types/api/admin";

// =============================================================================
// TYPES & CONSTANTS
// =============================================================================

interface AuditEventsTabProps {
    clientId: number;
}

const PAGE_SIZE = 25;

const ACTION_OPTIONS: SelectItemType[] = [
    { id: "", label: "All actions" },
    { id: "Create", label: "Create" },
    { id: "Update", label: "Update" },
    { id: "Delete", label: "Delete" },
    { id: "View", label: "View" },
    { id: "Export", label: "Export" },
    { id: "StateChange", label: "State Change" },
    { id: "Login", label: "Login" },
    { id: "Logout", label: "Logout" },
    { id: "ConfigChange", label: "Config Change" },
    { id: "ExceptionResolve", label: "Exception Resolve" },
    { id: "ExceptionSuppress", label: "Exception Suppress" },
    { id: "Reprocess", label: "Reprocess" },
    { id: "Purge", label: "Purge" },
    { id: "SecretRotation", label: "Secret Rotation" },
];

const ENTITY_TYPE_OPTIONS: SelectItemType[] = [
    { id: "", label: "All entity types" },
    { id: "Document", label: "Document" },
    { id: "LineItem", label: "Line Item" },
    { id: "File", label: "File" },
    { id: "FileStorage", label: "File Storage" },
    { id: "User", label: "User" },
    { id: "Organization", label: "Organization" },
    { id: "Client", label: "Client" },
    { id: "Workflow", label: "Workflow" },
    { id: "Rule", label: "Rule" },
    { id: "Job", label: "Job" },
    { id: "WorkflowExecution", label: "Workflow Execution" },
    { id: "WorkflowStep", label: "Workflow Step" },
    { id: "CustomField", label: "Custom Field" },
    { id: "Report", label: "Report" },
    { id: "Entitlement", label: "Entitlement" },
    { id: "RoutingCode", label: "Routing Code" },
    { id: "MailboxConfiguration", label: "Mailbox Configuration" },
];

const ACTOR_TYPE_OPTIONS: SelectItemType[] = [
    { id: "", label: "All actors" },
    { id: "User", label: "User" },
    { id: "System", label: "System" },
    { id: "Bridge", label: "Bridge" },
    { id: "Webhook", label: "Webhook" },
];

// =============================================================================
// HELPERS
// =============================================================================

function actionColor(action: string): "success" | "warning" | "error" | "gray" | "brand" {
    switch (action) {
        case "Create":
            return "success";
        case "Update":
        case "StateChange":
        case "Export":
        case "Reprocess":
            return "brand";
        case "Delete":
        case "Purge":
            return "error";
        case "ExceptionResolve":
        case "ExceptionSuppress":
        case "ConfigChange":
        case "SecretRotation":
            return "warning";
        default:
            return "gray";
    }
}

function formatJson(raw: string): string {
    try {
        return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
        return raw;
    }
}

function actorLabel(event: AuditEventAdminListItemDto): string {
    if (event.actorName) return event.actorName;
    if (event.actorType === "User" && event.actorId != null) return `User #${event.actorId}`;
    if (event.actorIdentifier) return `${event.actorType} (${event.actorIdentifier})`;
    return event.actorType;
}

function entityLabel(event: AuditEventAdminListItemDto): string {
    if (event.entityId == null) return event.entityType || "—";
    return `${event.entityType} #${event.entityId}`;
}

// =============================================================================
// EXPANDED ROW — full event detail (old/new values, metadata)
// =============================================================================

interface ExpandedRowProps {
    clientId: number;
    event: AuditEventAdminListItemDto;
    onViewEntityHistory: (entityType: string, entityId: number) => void;
}

function ExpandedRow({ clientId, event, onViewEntityHistory }: ExpandedRowProps) {
    const adminApi = useAdminApi();
    const [detail, setDetail] = useState<AuditEventAdminDetailDto | null>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(true);
    const [detailError, setDetailError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        async function fetchDetail() {
            setIsLoadingDetail(true);
            setDetailError(null);
            try {
                const d = await adminApi.getClientAuditEvent(clientId, event.id);
                if (!cancelled) setDetail(d);
            } catch (err) {
                if (!cancelled) setDetailError(err instanceof Error ? err.message : "Failed to load details.");
            } finally {
                if (!cancelled) setIsLoadingDetail(false);
            }
        }
        fetchDetail();
        return () => {
            cancelled = true;
        };
    }, [adminApi, clientId, event.id]);

    return (
        <div className="border-brand-secondary bg-primary_subtle space-y-4 rounded-lg border px-4 py-4">
            {isLoadingDetail ? (
                <LoadingIndicator size="sm" label="Loading details..." />
            ) : detailError ? (
                <p className="text-sm text-error-primary">{detailError}</p>
            ) : (
                <div className="space-y-3 text-sm">
                    {/* Identity & context */}
                    <div className="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-3">
                        <div>
                            <p className="text-xs font-medium text-secondary">Timestamp</p>
                            <p className="text-primary">{new Date(event.timestamp).toLocaleString()}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-secondary">Action</p>
                            <Badge color={actionColor(event.action)} size="sm" type="pill-color">
                                {event.action}
                            </Badge>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-secondary">Entity</p>
                            <p className="text-primary">
                                {entityLabel(event)}
                                {event.entityName && <span className="ml-1 text-tertiary">({event.entityName})</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-secondary">Actor</p>
                            <p className="text-primary">
                                {actorLabel(event)}
                                <span className="ml-1 text-tertiary">({event.actorType})</span>
                            </p>
                        </div>
                        {event.actorIdentifier && (
                            <div>
                                <p className="text-xs font-medium text-secondary">Actor Identifier</p>
                                <p className="text-primary">{event.actorIdentifier}</p>
                            </div>
                        )}
                        {event.correlationId != null && (
                            <div>
                                <p className="text-xs font-medium text-secondary">Correlation ID</p>
                                <p className="text-primary">{event.correlationId}</p>
                            </div>
                        )}
                    </div>

                    {/* Old / New values */}
                    {(detail?.oldValues || detail?.newValues) && (
                        <div className="grid grid-cols-1 gap-4 border-t border-secondary pt-3 lg:grid-cols-2">
                            <div>
                                <p className="mb-1 text-xs font-medium text-secondary">Old Values</p>
                                {detail?.oldValues ? (
                                    <pre className="max-h-80 overflow-y-auto rounded bg-secondary p-3 text-xs break-all whitespace-pre-wrap text-primary">
                                        {formatJson(detail.oldValues)}
                                    </pre>
                                ) : (
                                    <p className="text-tertiary">—</p>
                                )}
                            </div>
                            <div>
                                <p className="mb-1 text-xs font-medium text-secondary">New Values</p>
                                {detail?.newValues ? (
                                    <pre className="max-h-80 overflow-y-auto rounded bg-secondary p-3 text-xs break-all whitespace-pre-wrap text-primary">
                                        {formatJson(detail.newValues)}
                                    </pre>
                                ) : (
                                    <p className="text-tertiary">—</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Metadata */}
                    {detail?.metadata && (
                        <div className="border-t border-secondary pt-3">
                            <p className="mb-1 text-xs font-medium text-secondary">Metadata</p>
                            <pre className="max-h-80 overflow-y-auto rounded bg-secondary p-3 text-xs break-all whitespace-pre-wrap text-primary">
                                {formatJson(detail.metadata)}
                            </pre>
                        </div>
                    )}

                    {/* Entity history shortcut */}
                    {event.entityId != null && (
                        <div className="flex items-center gap-3 border-t border-secondary pt-3">
                            <p className="text-sm text-tertiary">See every audit event recorded for this {event.entityType.toLowerCase()}.</p>
                            <Button color="secondary" size="sm" onClick={() => onViewEntityHistory(event.entityType, event.entityId!)}>
                                View history
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// =============================================================================
// AUDIT EVENTS TAB
// =============================================================================

export function AuditEventsTab({ clientId }: AuditEventsTabProps) {
    const adminApi = useAdminApi();
    const [events, setEvents] = useState<AuditEventAdminListItemDto[]>([]);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    // Default to Document events — this tab's primary purpose is document history.
    const [entityTypeFilter, setEntityTypeFilter] = useState("Document");
    const [actionFilter, setActionFilter] = useState("");
    const [actorTypeFilter, setActorTypeFilter] = useState("");
    const [entityIdInput, setEntityIdInput] = useState("");
    const [entityIdFilter, setEntityIdFilter] = useState<number | undefined>(undefined);
    const [startDateFilter, setStartDateFilter] = useState("");
    const [endDateFilter, setEndDateFilter] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<number | null>(null);

    // The table can be wider than its scroll container; the expanded detail row is
    // pinned (sticky) to the visible width so it never requires horizontal scrolling.
    const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
    const [panelWidth, setPanelWidth] = useState<number | null>(null);

    useEffect(() => {
        if (!scrollEl) return;
        const update = () => setPanelWidth(scrollEl.clientWidth);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(scrollEl);
        return () => observer.disconnect();
    }, [scrollEl]);

    const fetchEvents = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await adminApi.listClientAuditEvents(clientId, {
                action: actionFilter || undefined,
                entityType: entityTypeFilter || undefined,
                entityId: entityIdFilter,
                actorType: actorTypeFilter || undefined,
                // Dates are sent as UTC day boundaries (audit timestamps are stored in UTC)
                startDate: startDateFilter ? `${startDateFilter}T00:00:00Z` : undefined,
                endDate: endDateFilter ? `${endDateFilter}T23:59:59.999Z` : undefined,
                pageSize: PAGE_SIZE,
                skip,
            });
            setEvents(res.data);
            setTotal(res.total);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load audit events.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId, actionFilter, entityTypeFilter, entityIdFilter, actorTypeFilter, startDateFilter, endDateFilter, skip]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    // Reset skip (and collapse any open row) when filters change
    const handleEntityTypeChange = useCallback((key: string | number | null) => {
        setEntityTypeFilter(String(key ?? ""));
        setSkip(0);
        setExpandedId(null);
    }, []);

    const handleActionChange = useCallback((key: string | number | null) => {
        setActionFilter(String(key ?? ""));
        setSkip(0);
        setExpandedId(null);
    }, []);

    const handleActorTypeChange = useCallback((key: string | number | null) => {
        setActorTypeFilter(String(key ?? ""));
        setSkip(0);
        setExpandedId(null);
    }, []);

    const applyEntityIdFilter = useCallback((raw: string) => {
        const trimmed = raw.trim();
        const parsed = trimmed ? Number(trimmed) : undefined;
        setEntityIdFilter(parsed !== undefined && Number.isFinite(parsed) ? parsed : undefined);
        setSkip(0);
        setExpandedId(null);
    }, []);

    const handleViewEntityHistory = useCallback((entityType: string, entityId: number) => {
        setEntityTypeFilter(entityType);
        setEntityIdInput(String(entityId));
        setEntityIdFilter(entityId);
        setActionFilter("");
        setActorTypeFilter("");
        setStartDateFilter("");
        setEndDateFilter("");
        setSkip(0);
        setExpandedId(null);
    }, []);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

    return (
        <div className="space-y-6">
            <section>
                <h3 className="text-lg font-semibold text-primary">Audit Events</h3>
                <p className="mt-1 text-sm text-tertiary">
                    Immutable audit trail for this client. Shows document events by default — filter by entity, action, actor or date to trace the full history
                    of any record.
                </p>
            </section>

            {/* Filters */}
            <div className="flex flex-wrap items-end gap-3">
                <div className="w-44">
                    <Select label="Entity type" items={ENTITY_TYPE_OPTIONS} selectedKey={entityTypeFilter} onSelectionChange={handleEntityTypeChange} size="sm">
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                </div>
                <div className="w-36">
                    <label className="mb-1 block text-xs font-medium text-secondary">Entity ID</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={entityIdInput}
                        onChange={(e) => setEntityIdInput(e.target.value)}
                        onBlur={() => applyEntityIdFilter(entityIdInput)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") applyEntityIdFilter(entityIdInput);
                        }}
                        placeholder="e.g. 123"
                        className="w-full rounded-md border border-secondary bg-primary px-3 py-1.5 text-sm text-primary placeholder-quaternary focus:border-brand focus:outline-none"
                    />
                </div>
                <div className="w-44">
                    <Select label="Action" items={ACTION_OPTIONS} selectedKey={actionFilter} onSelectionChange={handleActionChange} size="sm">
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                </div>
                <div className="w-40">
                    <Select label="Actor" items={ACTOR_TYPE_OPTIONS} selectedKey={actorTypeFilter} onSelectionChange={handleActorTypeChange} size="sm">
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                </div>
                <div className="w-40">
                    <label className="mb-1 block text-xs font-medium text-secondary">From (UTC)</label>
                    <input
                        type="date"
                        value={startDateFilter}
                        onChange={(e) => {
                            setStartDateFilter(e.target.value);
                            setSkip(0);
                            setExpandedId(null);
                        }}
                        className="w-full rounded-md border border-secondary bg-primary px-3 py-1.5 text-sm text-primary focus:border-brand focus:outline-none"
                    />
                </div>
                <div className="w-40">
                    <label className="mb-1 block text-xs font-medium text-secondary">To (UTC)</label>
                    <input
                        type="date"
                        value={endDateFilter}
                        onChange={(e) => {
                            setEndDateFilter(e.target.value);
                            setSkip(0);
                            setExpandedId(null);
                        }}
                        className="w-full rounded-md border border-secondary bg-primary px-3 py-1.5 text-sm text-primary focus:border-brand focus:outline-none"
                    />
                </div>
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="py-8">
                    <LoadingIndicator size="sm" label="Loading audit events..." />
                </div>
            ) : error ? (
                <p className="text-sm text-error-primary">{error}</p>
            ) : events.length === 0 ? (
                <p className="py-8 text-center text-sm text-quaternary">No audit events found.</p>
            ) : (
                <>
                    <div ref={setScrollEl} className="overflow-x-auto rounded-lg border border-secondary">
                        <table className="w-full text-left text-sm">
                            <thead className="border-b border-secondary bg-secondary_subtle">
                                <tr>
                                    <th className="px-4 py-3 font-medium text-secondary">ID</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Timestamp</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Action</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Entity</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Entity Name</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Actor</th>
                                    <th className="w-24 px-4 py-3 font-medium text-secondary"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-secondary">
                                {events.map((event) => {
                                    const isExpanded = expandedId === event.id;
                                    return (
                                        <Fragment key={event.id}>
                                            <tr className="group hover:bg-primary_hover">
                                                <td className="px-4 py-3 text-tertiary">{event.id}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-tertiary">{new Date(event.timestamp).toLocaleString()}</td>
                                                <td className="px-4 py-3">
                                                    <Badge color={actionColor(event.action)} size="sm" type="pill-color">
                                                        {event.action}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-tertiary">{entityLabel(event)}</td>
                                                <td className="max-w-xs truncate px-4 py-3 text-tertiary" title={event.entityName ?? undefined}>
                                                    {event.entityName ?? "—"}
                                                </td>
                                                <td className="max-w-xs truncate px-4 py-3 text-tertiary" title={actorLabel(event)}>
                                                    {actorLabel(event)}
                                                </td>
                                                <td className="w-24 px-4 py-3">
                                                    <Button
                                                        color={isExpanded ? "primary" : "secondary"}
                                                        size="sm"
                                                        onClick={() => setExpandedId(isExpanded ? null : event.id)}
                                                    >
                                                        {isExpanded ? "Close" : "Details"}
                                                    </Button>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr>
                                                    <td colSpan={7} className="pt-0 pb-4">
                                                        <div className="sticky left-0 px-4" style={panelWidth ? { width: panelWidth } : undefined}>
                                                            <ExpandedRow clientId={clientId} event={event} onViewEntityHistory={handleViewEntityHistory} />
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {total > PAGE_SIZE && (
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-tertiary">
                                Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of {total}
                            </p>
                            <div className="flex gap-2">
                                <Button color="secondary" size="sm" isDisabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}>
                                    Previous
                                </Button>
                                <Button color="secondary" size="sm" isDisabled={currentPage >= totalPages} onClick={() => setSkip(skip + PAGE_SIZE)}>
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
